import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import {
  Modules,
  AuthRoutes,
  RouteParamsTypes,
  PermissionModulesEnum,
  PermissionActionsEnum,
  RowClassName,
} from '../../../../enums';
import { hasResourseAccess } from '../../../../utils/methods';
import { AuthContext } from '../../../../contexts/AuthContext';
import { TableContext } from '../../../../contexts/TableContext';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import Badge from '../../../../components/Badge';
import Button from '../../../../components/Button';
import ElipsisCell from '../../../../components/TableBox/Cells/EllipsisCell';
import Link from '../../../../components/Link';
import ElipsisText from '../../../../components/ElipsisText';

interface RowProps {
  item: any;
  className?: RowClassName;
  onDeleteClick?: (item: any) => void;
}

const Row: React.FC<RowProps> = ({ item, className, onDeleteClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSystemAdmin, permissions } = useContext(AuthContext);
  const { headCells } = useContext(TableContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Properties]: [PermissionActionsEnum.Update] }],
  });

  const hasDeleteAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Properties]: [PermissionActionsEnum.Delete] }],
  });

  const handleEditButtonClick = (id: string, e?: any) => {
    const url = Modules.properties + AuthRoutes.properties + RouteParamsTypes.edit + '/' + id;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const handleViewClick = (id: string, e?: any) => {
    const url = Modules.properties + AuthRoutes.properties + RouteParamsTypes.view + '/' + id;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        // 1. NAME COLUMN (Name, Possession Date, RERA)
        if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex flex-column">
                <ElipsisText
                  width="100%"
                  text={item.name || '-'}
                  node={<Link text={item.name || '-'} />}
                  className="h6 mb-1 text-decoration-underline hyperlink"
                  onClick={(e) => handleViewClick(item.id, e)}
                />
                <small className="text-muted mb-1">{item.possession_date || '-'}</small>
              </div>
            </td>
          );
        }

        // 2. RERA NUMBER COLUMN
        if (headCell.id === VALIDATION_FIELD_MAPPING.rera_number) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.rera_number || '-'} />;
        }

        // 2. LOCATION COLUMN (City, Area) - using addressCity ID
        if (headCell.id === VALIDATION_FIELD_MAPPING.addressCity) {
           const areaNamesList = item.property_areas?.map((area: any) => area.name) || [];
           const allAreas = areaNamesList.join(' | ');
           
           let displayAreas = '';
           if (areaNamesList.length > 2) {
               displayAreas = areaNamesList.slice(0, 2).join(' | ') + '...';
           } else {
               displayAreas = allAreas;
           }

           return (
            <td key={headCell.id} width={headCell.width}>
                <div className="d-flex flex-column">
                   <span className="mb-1">{item.city?.name || '-'}</span>
                   {areaNamesList.length > 0 && (
                      <Tooltip title={allAreas} arrow>
                         <small className="text-muted text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                             {displayAreas}
                         </small>
                      </Tooltip>
                   )}
                </div>
            </td>
           );
        }

        // 3. DEVELOPER COLUMN (Property Developer Name, Tooltip: Multi-select developers)
        if (headCell.id === VALIDATION_FIELD_MAPPING.property_developer_name) {
          const devNames = item.developers?.map((d: any) => d.name) || [];
          if (item.property_developer_name) {
            devNames.unshift(item.property_developer_name);
          }
          const tooltipText = Array.from(new Set(devNames)).join(' | ');

          return (
            <td key={headCell.id} width={headCell.width}>
              <Tooltip title={tooltipText} arrow>
                <div
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.property_developer_name || '-'}
                </div>
              </Tooltip>
            </td>
          );
        }

        // 4. CATEGORY COLUMN
        if (headCell.id === VALIDATION_FIELD_MAPPING.category) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.category?.name || '-'} />;
        }
        
        // 5. CONSTRUCTION STATUS
        if (headCell.id === VALIDATION_FIELD_MAPPING.construction_status) {
             return <ElipsisCell key={headCell.id} width={headCell.width} text={item.construction_status_detail?.name || '-'} />;
        }

        // 5.1 APPROVAL STATUS
        if (headCell.id === VALIDATION_FIELD_MAPPING.approval_status) {
             return <ElipsisCell key={headCell.id} width={headCell.width} text={item.approval_status_detail?.name || '-'} />;
        }

        // 6. CONTACT PERSON (Name, Number)
        if (headCell.id === VALIDATION_FIELD_MAPPING.contact_person) {
            // Find primary contact
            const primaryContact = item.property_contact_details?.find((c: any) => c.is_primary) || item.property_contact_details?.[0];
            return (
                 <td key={headCell.id} width={headCell.width}>
                    {primaryContact ? (
                        <div className="d-flex flex-column">
                            <span className="mb-1">{primaryContact.contact_person}</span>
                            <small className="text-muted">{primaryContact.contact_number}</small>
                        </div>
                    ) : (
                        '-'
                    )}
                 </td>
            )
        }

        // 7. STATUS COLUMN
        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={item.is_active ? t('common.status.active') : t('common.status.inactive')}
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        // 8. ACTIONS COLUMN
        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-center">
                {hasEditAccess ? (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-edit-box-line ri-20px"></i>}
                    onClick={(e) => handleEditButtonClick(item.id, e)}
                  />
                ) : (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-eye-line ri-20px"></i>}
                    onClick={(e) => handleViewClick(item.id, e)}
                  />
                )}
                {hasDeleteAccess && (
                  <Tooltip title={t('common.actions.delete') || 'Delete'} placement="top" arrow>
                    <span>
                      <Button
                        className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
                        icon={<i className="ri-delete-bin-line ri-20px"></i>}
                        onClick={() => onDeleteClick?.(item)}
                      />
                    </span>
                  </Tooltip>
                )}
                 <Tooltip
                  title={
                    <>
                      <div>
                        {t('common.table.lastUpdatedBy')} {item.updater?.name || '-'}
                      </div>
                      <div>
                        {t('common.table.lastUpdated')} {item.updated_at || '-'}
                      </div>
                      {item.creator && (
                        <div className="mt-2">
                          {t('common.table.createdBy')} {item.creator.name}
                        </div>
                      )}
                      <div className={!item.creator ? 'mt-2' : ''}>
                        {t('common.table.created')} {item.created_at || '-'}
                      </div>
                    </>
                  }
                  arrow
                  classes={{
                    tooltip: 'tooltip',
                  }}
                >
                  <i className="ri-timer-line ri-20px"></i>
                </Tooltip>
              </div>
            </td>
          );
        }

        return <ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] || '-'} />;
      })}
    </tr>
  );
};

export default Row;
